import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useProducts, useDeleteProduct, useProductCategories, useDeleteCategory } from "@/hooks/use-products";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useDebounce } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CategoryDialog } from "@/components/products/category-dialog";
import { Search, Plus, Pencil, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { TablePagination } from "@/components/ui/table-pagination";

const stockColor: Record<string, string> = {
  instock: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  outofstock: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  onbackorder: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
};

const stockLabel: Record<string, string> = {
  instock: "Na stanju",
  outofstock: "Nedostupno",
  onbackorder: "Narudžba",
};

const typeLabels: Record<string, string> = {
  simple: "Jednostavni",
  variable: "Varijabilni",
  grouped: "Grupni",
  external: "Vanjski",
};

function sortCategoriesByHierarchy(categories: any[]): (any & { depth: number })[] {
  const result: (any & { depth: number })[] = [];

  function addChildren(parentId: number, depth: number) {
    const children = categories
      .filter((c) => c.parent === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));
    for (const child of children) {
      result.push({ ...child, depth });
      addChildren(child.id, depth + 1);
    }
  }

  addChildren(0, 0);
  return result;
}

export function ProductsPage() {
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [productType, setProductType] = useState("");
  const [productStatus, setProductStatus] = useState("publish"); // active products by default
  const debouncedSearch = useDebounce(search);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const deleteMut = useDeleteProduct(storeId);
  const deleteCatMut = useDeleteCategory(storeId);

  const isAdmin = hasRole(user, "admin") || hasRole(user, "manager");

  const filters: Record<string, string> = { page: String(page), per_page: "20" };
  if (debouncedSearch) filters.search = debouncedSearch;
  if (category) filters.category = category;
  if (stockStatus) filters.stock_status = stockStatus;
  if (productType) filters.type = productType;
  if (productStatus) filters.status = productStatus;

  const { data, isLoading, error } = useProducts(storeId, filters);
  const { data: categories } = useProductCategories(storeId);

  async function handleDelete(product: any) {
    if (!confirm(`Obrisati proizvod "${product.name}"?`)) return;
    try {
      await deleteMut.mutateAsync(product.id);
      toast.success("Proizvod obrisan");
    } catch (err: any) {
      toast.error(err?.message || "Greška pri brisanju");
    }
  }

  function handleEditCategory(cat: any) {
    setEditingCategory(cat);
    setCategoryDialogOpen(true);
  }

  function handleCreateCategory() {
    setEditingCategory(null);
    setCategoryDialogOpen(true);
  }

  function handleCategoryDialogClose(open: boolean) {
    setCategoryDialogOpen(open);
    if (!open) setEditingCategory(null);
  }

  async function handleDeleteCategory(cat: any) {
    if (!confirm(`Obrisati kategoriju "${cat.name}"?`)) return;
    try {
      await deleteCatMut.mutateAsync(cat.id);
      toast.success("Kategorija obrisana");
    } catch (err: any) {
      toast.error(err?.message || "Greška pri brisanju kategorije");
    }
  }

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-xs">Odaberite trgovinu</p>
      </div>
    );
  }

  const sortedCategories = categories ? sortCategoriesByHierarchy(categories) : [];

  return (
    <div className="p-4 flex flex-col gap-3">
      <Tabs defaultValue="products">
        <div className="flex items-center justify-between">
          <TabsList variant="line">
            <TabsTrigger value="products">Proizvodi</TabsTrigger>
            <TabsTrigger value="categories">Kategorije</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="products" className="flex flex-col gap-3">
          {isAdmin && (
            <div className="flex justify-end">
              <Link to="/products/new">
                <Button size="sm">
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Novi proizvod
                </Button>
              </Link>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="h-8 text-xs pl-8"
                placeholder="Pretraži po nazivu ili SKU..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select value={category} onValueChange={(v) => { setCategory(v === "sve" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-8 text-xs w-full sm:w-[160px]">
                <SelectValue placeholder="Kategorija" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sve">Sve kategorije</SelectItem>
                {categories?.map((c: any) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stockStatus} onValueChange={(v) => { setStockStatus(v === "sve" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-8 text-xs w-full sm:w-[150px]">
                <SelectValue placeholder="Status zaliha" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sve">Svi statusi</SelectItem>
                <SelectItem value="instock">Na stanju</SelectItem>
                <SelectItem value="outofstock">Nedostupno</SelectItem>
                <SelectItem value="onbackorder">Narudžba</SelectItem>
              </SelectContent>
            </Select>
            <Select value={productType} onValueChange={(v) => { setProductType(v === "svi" ? "" : v); setPage(1); }}>
              <SelectTrigger className="h-8 text-xs w-full sm:w-[140px]">
                <SelectValue placeholder="Tip" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="svi">Svi tipovi</SelectItem>
                <SelectItem value="simple">Jednostavni</SelectItem>
                <SelectItem value="variable">Varijabilni</SelectItem>
                <SelectItem value="grouped">Grupni</SelectItem>
                <SelectItem value="external">Vanjski</SelectItem>
              </SelectContent>
            </Select>
            <Select value={productStatus} onValueChange={(v) => { setProductStatus(v); setPage(1); }}>
              <SelectTrigger className="h-8 text-xs w-full sm:w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="publish">Aktivni</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="any">Svi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : (
            <div className="ring-1 ring-foreground/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left font-medium">Proizvod</th>
                    <th className="p-2 text-right font-medium">Cijena</th>
                    <th className="p-2 text-left font-medium hidden sm:table-cell">Zaliha</th>
                    <th className="p-2 text-left font-medium hidden md:table-cell">Kategorije</th>
                    <th className="p-2 text-left font-medium hidden lg:table-cell">Tip</th>
                    {isAdmin && <th className="p-2 text-right font-medium w-20">Akcije</th>}
                  </tr>
                </thead>
                <tbody>
                  {data?.products?.map((p: any) => (
                    <tr key={p.id} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {p.image ? (
                            <img src={p.image} alt="" className="w-8 h-8 object-cover shrink-0" />
                          ) : (
                            <div className="w-8 h-8 bg-muted shrink-0 flex items-center justify-center">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium truncate">{p.name}</span>
                            {p.sku && (
                              <span className="text-muted-foreground font-mono text-[10px]">{p.sku}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex flex-col items-end">
                          {p.type === "variable" && p.price_min ? (
                            <span className="font-medium">
                              {p.price_min === p.price_max
                                ? `${p.price_min} KM`
                                : `${p.price_min} – ${p.price_max} KM`}
                            </span>
                          ) : p.sale_price ? (
                            <>
                              <span className="line-through text-muted-foreground">{(p.regular_price || p.price)} KM</span>
                              <span className="font-medium text-destructive">{p.sale_price} KM</span>
                            </>
                          ) : (() => {
                            const shown = p.regular_price || p.price;
                            return <span className="font-medium">{shown ? `${shown} KM` : "—"}</span>;
                          })()}
                        </div>
                      </td>
                      <td className="p-2 hidden sm:table-cell">
                        <Badge className={`text-[10px] px-1.5 py-0 ${stockColor[p.stock_status] || ""}`}>
                          {stockLabel[p.stock_status] || p.stock_status || "—"}
                        </Badge>
                      </td>
                      <td className="p-2 hidden md:table-cell">
                        <div className="flex flex-wrap gap-0.5">
                          {p.categories?.slice(0, 3).map((cat: any) => (
                            <Badge key={cat.id} variant="outline" className="text-[10px] px-1.5 py-0">
                              {cat.name}
                            </Badge>
                          ))}
                          {p.categories?.length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              +{p.categories.length - 3}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-2 hidden lg:table-cell">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {typeLabels[p.type] || p.type}
                        </Badge>
                      </td>
                      {isAdmin && (
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link to="/products/$productId" params={{ productId: String(p.id) }}>
                              <Button variant="ghost" size="icon-xs" title="Uredi">
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => handleDelete(p)}
                              title="Obriši"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {data?.products?.length === 0 && (
                    <tr>
                      <td colSpan={isAdmin ? 6 : 5} className="p-6 text-center text-muted-foreground">
                        Nema proizvoda
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {data && <TablePagination page={page} totalPages={data.totalPages} onPageChange={setPage} />}
        </TabsContent>

        <TabsContent value="categories" className="flex flex-col gap-3">
          {isAdmin && (
            <div className="flex justify-end">
              <Button size="sm" onClick={handleCreateCategory}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nova kategorija
              </Button>
            </div>
          )}

          <div className="ring-1 ring-foreground/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left font-medium">Naziv</th>
                  <th className="p-2 text-left font-medium hidden sm:table-cell">Slug</th>
                  <th className="p-2 text-left font-medium hidden md:table-cell">Nadkategorija</th>
                  <th className="p-2 text-right font-medium">Proizvoda</th>
                  {isAdmin && <th className="p-2 text-right font-medium w-20">Akcije</th>}
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((cat) => {
                  const parentCat = categories?.find((c: any) => c.id === cat.parent);
                  return (
                    <tr key={cat.id} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="p-2">
                        <span style={{ paddingLeft: `${cat.depth * 16}px` }} className="font-medium">
                          {cat.name}
                        </span>
                      </td>
                      <td className="p-2 hidden sm:table-cell">
                        <span className="text-muted-foreground font-mono">{cat.slug}</span>
                      </td>
                      <td className="p-2 hidden md:table-cell">
                        <span className="text-muted-foreground">{parentCat?.name || "—"}</span>
                      </td>
                      <td className="p-2 text-right">{cat.count}</td>
                      {isAdmin && (
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => handleEditCategory(cat)}
                              title="Uredi"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => handleDeleteCategory(cat)}
                              title="Obriši"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {sortedCategories.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="p-6 text-center text-muted-foreground">
                      Nema kategorija
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={handleCategoryDialogClose}
        category={editingCategory}
        categories={categories || []}
      />
    </div>
  );
}
